import { useCallback, useEffect, useRef, useState } from 'react';
import { customerAPI } from '@matix/api-client';
import type { Customer, CustomerAddress } from '@matix/types';
import { useLang } from '../../lib/language';
import { phoneMatchesCaller, takeCustomerCallPhone } from '../../lib/customerCallLink';
import { toast } from '../../store/toast';
import { callCenterApi, ccErrorMessage } from './api';
import { useCallCenter } from './store';
import { LookupCombo } from '../customers/LookupCombo';
import { useCountries, useDistricts, useGovernorates } from '../customers/hooks';

/**
 * Step 1 — who is calling, and where the food is going.
 *
 * The phone number is the only entry point on purpose: it is the one thing a
 * caller always volunteers, and it is the key the customer book is actually
 * indexed on. Everything else in this panel exists so an agent never has to
 * leave the call to finish it — a caller who is not in the book yet, or whose
 * address has changed, is the ordinary case at a call centre, not an exception
 * to hand off to somebody with a till.
 *
 * Creating and editing go through the SAME `/api/v1/customers` endpoints the
 * till uses, not a call-centre copy: one place decides what a valid customer
 * is, and an address added here is the same row the driver's sheet reads.
 */
export function CustomerPanel(): JSX.Element {
  const { t, lang } = useLang();
  const customer = useCallCenter((s) => s.customer);
  const setCustomer = useCallCenter((s) => s.setCustomer);
  const address = useCallCenter((s) => s.address);
  const setAddress = useCallCenter((s) => s.setAddress);
  const settings = useCallCenter((s) => s.bootstrap?.settings ?? null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | 'new' | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      return;
    }
    let alive = true;
    setSearching(true);
    const id = setTimeout(() => {
      void callCenterApi.agent
        .searchCustomers(q)
        .then((r) => alive && setResults(r.customers))
        .catch(() => alive && setResults([]))
        .finally(() => alive && setSearching(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [query]);

  /** Always re-fetch: the search list carries no addresses, by contract. */
  const attach = useCallback(
    async (c: Customer) => {
      try {
        setCustomer(await callCenterApi.agent.getCustomer(c.id));
      } catch {
        setCustomer(c);
      }
      setQuery('');
      setResults([]);
    },
    [setCustomer],
  );

  // The phone-system link (/customer-call?phoneNo=…, lib/customerCallLink):
  // the caller is attached when the book has them, else the new-customer form
  // opens with their number. Never replaces a caller the agent already has.
  useEffect(() => {
    const phone = takeCustomerCallPhone();
    if (!phone || useCallCenter.getState().customer) return;
    void (async () => {
      const q = phone.length > 8 ? phone.slice(-8) : phone;
      let match: Customer | undefined;
      try {
        match = (await callCenterApi.agent.searchCustomers(q)).customers.find((c) => phoneMatchesCaller(c.phone, phone));
      } catch {
        /* fall through to the form; its duplicate check still guards the phone */
      }
      if (match) {
        void attach(match);
      } else {
        setQuery(phone);
        setCreating(true);
      }
    })();
  }, [attach]);

  async function reloadCustomer(): Promise<void> {
    if (!customer) return;
    try {
      const fresh = await callCenterApi.agent.getCustomer(customer.id);
      // Keep the agent on the address they were editing rather than snapping
      // back to the first one.
      const keep = address ? fresh.addresses?.find((a) => a.id === address.id) ?? null : null;
      setCustomer(fresh);
      if (keep) setAddress(keep);
    } catch {
      /* the panel still shows what it had */
    }
  }

  // ── No caller yet ────────────────────────────────────────────────────────
  if (!customer) {
    return (
      <section className="cc-step cc-panel-card">
        <h3>
          <span className="cc-step-n">1</span>
          {t({ en: 'Who is calling?', ar: 'من المتصل؟' })}
        </h3>
        <input
          className="cc-phone-input"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t({ en: 'Phone number or name', ar: 'رقم الهاتف أو الاسم' })}
          inputMode="tel"
        />

        {searching && <div className="cc-hint">{t({ en: 'Searching…', ar: 'جارٍ البحث…' })}</div>}

        {results.length > 0 && (
          <ul className="cc-results">
            {results.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => void attach(c)}>
                  <span className="cc-results-name">
                    {(lang === 'ar' ? c.name_ar || c.name : c.name) ?? '—'}
                  </span>
                  <span className="cc-results-phone">{c.phone ?? ''}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {query.trim().length >= 3 && !searching && (
          <button type="button" className="cc-btn cc-btn--primary cc-newcust" onClick={() => setCreating(true)}>
            {t({ en: 'New customer', ar: 'عميل جديد' })}
            {results.length === 0 ? ` · ${query.trim()}` : ''}
          </button>
        )}

        {creating && (
          <NewCustomerForm
            initialPhone={/^[0-9+\s-]+$/.test(query.trim()) ? query.trim() : ''}
            initialName={/^[0-9+\s-]+$/.test(query.trim()) ? '' : query.trim()}
            onCancel={() => setCreating(false)}
            onCreated={(c) => {
              setCreating(false);
              void attach(c);
            }}
          />
        )}
      </section>
    );
  }

  // ── Caller attached ──────────────────────────────────────────────────────
  const addresses = customer.addresses ?? [];
  const needsAddress =
    (settings?.defaultOrderType ?? 2) === 2 && (settings?.requireAddress ?? true) && !address;

  return (
    <section className="cc-step cc-step--done cc-panel-card">
      <h3>
        <span className="cc-step-n">1</span>
        {t({ en: 'Customer', ar: 'العميل' })}
      </h3>

      <div className="cc-picked">
        <div>
          <div className="cc-picked-name">
            {(lang === 'ar' ? customer.name_ar || customer.name : customer.name) ?? '—'}
          </div>
          <div className="cc-picked-sub">{customer.phone ?? ''}</div>
          {customer.stats && (
            <div className="cc-picked-stats">
              {t({ en: 'Orders', ar: 'طلبات' })} {customer.stats.visits ?? 0}
            </div>
          )}
        </div>
        <button type="button" className="cc-link" onClick={() => setCustomer(null)}>
          {t({ en: 'Change', ar: 'تغيير' })}
        </button>
      </div>

      <div className="cc-addr-head">
        <span>{t({ en: 'Deliver to', ar: 'التوصيل إلى' })}</span>
        <button type="button" className="cc-link" onClick={() => setEditingAddress('new')}>
          + {t({ en: 'Add address', ar: 'إضافة عنوان' })}
        </button>
      </div>

      {addresses.length === 0 ? (
        <div className={`cc-hint ${needsAddress ? 'cc-hint--warn' : ''}`}>
          {t({
            en: 'No address on file. Add one to send a delivery order.',
            ar: 'لا يوجد عنوان محفوظ. أضف عنواناً لإرسال طلب توصيل.',
          })}
        </div>
      ) : (
        <ul className="cc-addr-list">
          {addresses.map((a) => (
            <li key={a.id} className={address?.id === a.id ? 'cc-addr--on' : ''}>
              <button type="button" className="cc-addr-pick" onClick={() => setAddress(a)}>
                <span className="cc-addr-line">{addressLabel(a, lang)}</span>
                {a.address_note && <span className="cc-addr-note">{a.address_note}</span>}
              </button>
              <button
                type="button"
                className="cc-link cc-addr-edit"
                onClick={() => setEditingAddress(a)}
              >
                {t({ en: 'Edit', ar: 'تعديل' })}
              </button>
            </li>
          ))}
        </ul>
      )}

      {editingAddress && (
        <AddressEditor
          customerId={customer.id}
          address={editingAddress === 'new' ? null : editingAddress}
          onCancel={() => setEditingAddress(null)}
          onSaved={async (saved) => {
            setEditingAddress(null);
            await reloadCustomer();
            setAddress(saved);
          }}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function NewCustomerForm({
  initialPhone,
  initialName,
  onCancel,
  onCreated,
}: {
  initialPhone: string;
  initialName: string;
  onCancel: () => void;
  onCreated: (c: Customer) => void;
}): JSX.Element {
  const { t } = useLang();
  const [phone, setPhone] = useState(initialPhone);
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);

  async function save(): Promise<void> {
    if (!phone.trim()) return;
    setSaving(true);
    try {
      const created = await customerAPI.createCash({
        phone: phone.trim(),
        name: name.trim() || phone.trim(),
      });
      toast(t({ en: 'Customer created', ar: 'تم إنشاء العميل' }), 'success');
      onCreated(created);
    } catch (err) {
      toast(ccErrorMessage(err, 'Could not create the customer'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="cc-inline-form">
      <label className="cc-field">
        <span>{t({ en: 'Phone', ar: 'الهاتف' })}</span>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoFocus />
      </label>
      <label className="cc-field">
        <span>{t({ en: 'Name', ar: 'الاسم' })}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="cc-inline-form-actions">
        <button type="button" className="cc-btn" onClick={onCancel}>
          {t({ en: 'Cancel', ar: 'إلغاء' })}
        </button>
        <button
          type="button"
          className="cc-btn cc-btn--primary"
          disabled={saving || !phone.trim()}
          onClick={() => void save()}
        >
          {saving ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Create', ar: 'إنشاء' })}
        </button>
      </div>
    </div>
  );
}

/**
 * Add or edit one delivery address.
 *
 * Free-text fields only. The till's full address form carries the Saudi
 * National Address layout, a city/district picker backed by two lookup tables
 * and a Google resolver — all of which are the right thing at a counter with a
 * customer's ID in hand, and the wrong thing for an agent typing what someone
 * is saying down a phone. The columns written are the same either way, so an
 * address added here opens normally in the till's editor.
 */
function AddressEditor({
  customerId,
  address,
  onCancel,
  onSaved,
}: {
  customerId: number;
  address: CustomerAddress | null;
  onCancel: () => void;
  onSaved: (saved: CustomerAddress) => void | Promise<void>;
}): JSX.Element {
  const { t } = useLang();
  const { defaultCountryId } = useCountries();

  const [countryId, setCountryId] = useState<number | null>(address?.country_id ?? null);
  const [governorateId, setGovernorateId] = useState<number | null>(
    address?.governorate_id ?? null,
  );
  const [areaId, setAreaId] = useState<number | null>(address?.area_id ?? null);
  const [block, setBlock] = useState(address?.block ?? '');
  const [street, setStreet] = useState(address?.street ?? '');
  const [avenue, setAvenue] = useState(address?.avenue ?? '');
  const [house, setHouse] = useState(address?.house ?? '');
  const [floor, setFloor] = useState(address?.floor ?? '');
  const [flat, setFlat] = useState(address?.flat ?? '');
  const [note, setNote] = useState(address?.address_note ?? '');
  const [saving, setSaving] = useState(false);

  // A NEW address starts on the tenant's own country so the city list is not
  // empty on open; an EXISTING one keeps whatever it was saved with rather than
  // being silently rewritten — the same rule the till's form follows.
  useEffect(() => {
    if (!address && countryId == null && defaultCountryId != null) setCountryId(defaultCountryId);
  }, [address, countryId, defaultCountryId]);

  const { data: cities, isLoading: citiesLoading } = useGovernorates(countryId);
  const { data: districts, isLoading: districtsLoading } = useDistricts(governorateId);

  // A saved address can carry an area with no city: GovernorateId went unused
  // before the city/district layout existed. Resolve the city from the area so
  // the card opens populated instead of showing "Select a city first" over an
  // area that is already set. Same back-fill the till's form does.
  const backfilled = useRef(false);
  useEffect(() => {
    if (backfilled.current) return;
    if (governorateId != null || areaId == null) return;
    backfilled.current = true;
    let cancelled = false;
    void customerAPI
      .getAreaNames([areaId])
      .then((res) => {
        const id = res.data[0]?.governorate_id;
        if (!cancelled && id != null) setGovernorateId(id);
      })
      .catch(() => {
        /* Non-fatal — the agent can still pick the city by hand. */
      });
    return () => {
      cancelled = true;
    };
  }, [areaId, governorateId]);

  const filled =
    areaId != null ||
    governorateId != null ||
    [block, street, avenue, house, floor, flat, note].some((v) => v.trim() !== '');

  async function save(): Promise<void> {
    if (!filled) return;
    setSaving(true);
    const v = (x: string): string | null => x.trim() || null;
    const payload = {
      country_id: countryId,
      governorate_id: governorateId,
      area_id: areaId,
      block: v(block),
      street: v(street),
      avenue: v(avenue),
      house: v(house),
      floor: v(floor),
      flat: v(flat),
      address_note: v(note),
    };
    try {
      const saved = address
        ? await customerAPI.updateAddress(customerId, address.id, payload)
        : await customerAPI.addAddress(customerId, payload);
      toast(
        address
          ? t({ en: 'Address updated', ar: 'تم تحديث العنوان' })
          : t({ en: 'Address added', ar: 'تمت إضافة العنوان' }),
        'success',
      );
      await onSaved(saved);
    } catch (err) {
      toast(ccErrorMessage(err, 'Could not save the address'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="cc-inline-form">
      {/* City and area are PICKERS, not free text: they are `GL_Governorate` /
          `GL_Area` rows, the driver's sheet and the delivery reports group by
          them, and a typed "Salmiya" that matches no row groups with nothing.
          Both carry the same inline "+ Add" the till's form has — an agent on a
          live call cannot be told to go and ask an admin to create a district. */}
      <div className="cc-addr-grid">
        <label className="cc-field cc-field--wide">
          <span>{t({ en: 'City', ar: 'المدينة' })}</span>
          <LookupCombo
            value={governorateId}
            options={cities}
            loading={citiesLoading}
            disabled={saving}
            placeholder={t({ en: 'Select city', ar: 'اختر المدينة' })}
            createLabel={t({ en: 'Add city', ar: 'إضافة مدينة' })}
            onCreate={async (name) => {
              const created = await customerAPI.createGovernorate({
                name,
                name_ar: name,
                country_id: countryId,
              });
              return created;
            }}
            onChange={(id) => {
              // Areas hang off the city, so changing it drops an area that no
              // longer belongs.
              if (id !== governorateId) setAreaId(null);
              setGovernorateId(id);
            }}
          />
        </label>

        <label className="cc-field cc-field--wide">
          <span>{t({ en: 'Area', ar: 'المنطقة' })}</span>
          <LookupCombo
            value={areaId}
            options={districts}
            loading={districtsLoading}
            disabled={saving || governorateId == null}
            placeholder={
              governorateId == null
                ? t({ en: 'Select a city first', ar: 'اختر المدينة أولًا' })
                : t({ en: 'Select area', ar: 'اختر المنطقة' })
            }
            createLabel={t({ en: 'Add area', ar: 'إضافة منطقة' })}
            onCreate={async (name) => {
              const created = await customerAPI.createArea({
                name,
                name_ar: name,
                governorate_id: governorateId,
              });
              return created;
            }}
            onChange={setAreaId}
          />
        </label>

        <label className="cc-field">
          <span>{t({ en: 'Block', ar: 'القطعة' })}</span>
          <input value={block} onChange={(e) => setBlock(e.target.value)} />
        </label>
        <label className="cc-field">
          <span>{t({ en: 'Street', ar: 'الشارع' })}</span>
          <input value={street} onChange={(e) => setStreet(e.target.value)} />
        </label>
        <label className="cc-field">
          <span>{t({ en: 'Avenue', ar: 'الجادة' })}</span>
          <input value={avenue} onChange={(e) => setAvenue(e.target.value)} />
        </label>
        <label className="cc-field">
          <span>{t({ en: 'Building', ar: 'المبنى' })}</span>
          <input value={house} onChange={(e) => setHouse(e.target.value)} />
        </label>
        <label className="cc-field">
          <span>{t({ en: 'Floor', ar: 'الطابق' })}</span>
          <input value={floor} onChange={(e) => setFloor(e.target.value)} />
        </label>
        <label className="cc-field">
          <span>{t({ en: 'Flat', ar: 'الشقة' })}</span>
          <input value={flat} onChange={(e) => setFlat(e.target.value)} />
        </label>
      </div>
      <label className="cc-field">
        <span>{t({ en: 'Landmark / note for the driver', ar: 'علامة مميزة / ملاحظة للسائق' })}</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="cc-inline-form-actions">
        <button type="button" className="cc-btn" onClick={onCancel}>
          {t({ en: 'Cancel', ar: 'إلغاء' })}
        </button>
        <button
          type="button"
          className="cc-btn cc-btn--primary"
          disabled={saving || !filled}
          onClick={() => void save()}
        >
          {saving ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Save address', ar: 'حفظ العنوان' })}
        </button>
      </div>
    </div>
  );
}

/**
 * One line naming everything the address actually holds.
 *
 * City → area → block → street → avenue → building → floor → flat is the order
 * the legacy POS prints it and the order a driver reads it: widest first, so
 * the line can be scanned from the left until it is specific enough. Every
 * field the row carries appears — a summary that quietly drops the avenue is
 * indistinguishable, to the agent reading it back to the caller, from an
 * address that never had one.
 *
 * The labelled prefixes matter for the same reason: "12 · 5 · 3" is three
 * numbers, "Block 12 · St 5 · Bldg 3" is an address.
 */
export function addressLabel(a: CustomerAddress, lang: string): string {
  const ar = lang === 'ar';
  const pick = (en: string | null | undefined, arabic: string | null | undefined): string =>
    ((ar ? arabic || en : en || arabic) ?? '').toString().trim();

  const label = (en: string, arabic: string): string => (ar ? arabic : en);
  const tagged = (tag: string, value: string | null | undefined): string | null => {
    const v = (value ?? '').toString().trim();
    return v ? `${tag} ${v}` : null;
  };

  const parts = [
    pick(a.governorate_name, a.governorate_name_ar),
    pick(a.area_name, a.area_name_ar),
    tagged(label('Block', 'قطعة'), a.block),
    tagged(label('St', 'شارع'), a.street),
    tagged(label('Ave', 'جادة'), a.avenue),
    tagged(label('Bldg', 'مبنى'), a.house),
    tagged(label('Fl', 'طابق'), a.floor),
    tagged(label('Flat', 'شقة'), a.flat),
  ]
    .map((p) => (p ?? '').trim())
    .filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : `#${a.id}`;
}
